import classNames from 'classnames';

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'transparent';
type ButtonSize = 'default' | 'large' | 'small' | 'mini';

export interface ButtonStyling {
  /**
   * The hierarchy level of the button.
   *
   * `default` for a regular button used in most cases.
   *
   * `primary` for a button used to indicate a primary action on a page.
   *
   * `secondary` for a button used to indicate a secondary action on a page or inside a module/card.
   *
   * `tertiary` for a button used to indicate at tertiary action on a page.
   *
   * `transparent` for a button used to indicate subtle actions or a button similar to a link
   */
  variant?: ButtonVariant;

  /**
   * The size of the button.
   */
  size?: ButtonSize;

  /** Will force the button to go the full width of its parent container */
  fullWidth?: boolean;

  /** Invert the button color when used on a contrasted background. */
  inverted?: boolean;
}

export const DEFAULT_SIZE = 'default';
export const DEFAULT_VARIANT = 'secondary';

/**
 * Function to generate className string to style any HTML element like an RCButton.
 * This is mainly for use on CoreLinks.
 *
 * @example
 * ```
 * <CoreLink
 *   to={something}
 *   className={buttonClassName({ variant: 'primary' })}
 * >
 *   I look like a button
 * </CoreLink>
 * ```
 */
export function buttonClassName({
  variant = DEFAULT_VARIANT,
  size = DEFAULT_SIZE,
  inverted,
  fullWidth,
}: ButtonStyling): string {
  return classNames(
    'rc-button',
    {
      // color and state modifiers
      'rc-button--primary': variant === 'primary',
      'rc-button--secondary': variant === 'secondary',
      'rc-button--tertiary': variant === 'tertiary',
      'rc-button--transparent': variant === 'transparent',

      // size modifiers
      'rc-button--large': size === 'large',
      'rc-button--small': size === 'small',
      'rc-button--mini': size === 'mini',

      // display modifiers
      'rc-button--inverted': inverted,
      'rc-button--full-width': fullWidth,
    },
  );
}
